import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { OutletContext } from './employer-settings-wrapper'
import { useSpecialPeriodsQuery } from '../../queries/special-periods'
import SpecialPeriodsList from '../../components/special-periods/list'
import SpecialPeriodModalForm from '../../components/special-periods/special-period-modal-form'
import { SpecialPeriod } from '../../api/special-periods'
import { Box, Element, Form } from 'react-bulma-components'
import SidebarContentTitle from '../../components/layout/sidebar/sidebar-content-title'
import { AddResourceIcon } from '../../components/icons'
import { isAuthorizedFor } from '../../components/protected/protected'

/**
 * Component for managing special periods for a specific employer
 * Only accessible to superAdmin
 * Uses the employer from the outlet context (no filters, no employer selection)
 */
const SpecialPeriodsSettings: React.FC = () => {
  const { employer } = useOutletContext<OutletContext>()
  const isSuperAdmin = isAuthorizedFor(['superAdmin'])

  // Query special periods for the current employer
  const { data: allSpecialPeriods, isLoading } = useSpecialPeriodsQuery(
    {
      employer: employer?._id,
    },
    {
      enabled: Boolean(employer),
    },
  )

  // Year filter state (default to current year)
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = React.useState<number | null>(currentYear)

  // Extract available years from special periods
  const availableYears = React.useMemo(() => {
    if (!allSpecialPeriods) return []
    const years = new Set<number>()
    allSpecialPeriods.forEach(period => {
      const startDate =
        typeof period.startDate === 'string' ? new Date(period.startDate) : period.startDate
      const endDate = typeof period.endDate === 'string' ? new Date(period.endDate) : period.endDate
      years.add(startDate.getFullYear())
      years.add(endDate.getFullYear())
    })
    return Array.from(years).sort((a, b) => b - a) // Sort descending (most recent first)
  }, [allSpecialPeriods])

  // Extract existing custom types for autocomplete suggestions
  const existingTypes = React.useMemo(() => {
    if (!allSpecialPeriods) return []
    return allSpecialPeriods
      .map(period => period.type)
      .filter((type): type is string => Boolean(type))
  }, [allSpecialPeriods])

  // Filter and sort special periods by selected year (sorted by start date, oldest first)
  const specialPeriods = React.useMemo(() => {
    if (!allSpecialPeriods) return []

    let filtered = allSpecialPeriods
    if (selectedYear !== null) {
      filtered = allSpecialPeriods.filter(period => {
        const startDate =
          typeof period.startDate === 'string' ? new Date(period.startDate) : period.startDate
        const endDate =
          typeof period.endDate === 'string' ? new Date(period.endDate) : period.endDate
        return startDate.getFullYear() === selectedYear || endDate.getFullYear() === selectedYear
      })
    }

    // Sort by start date (oldest first)
    return [...filtered].sort((a, b) => {
      const startDateA =
        typeof a.startDate === 'string' ? new Date(a.startDate).getTime() : a.startDate.getTime()
      const startDateB =
        typeof b.startDate === 'string' ? new Date(b.startDate).getTime() : b.startDate.getTime()
      return startDateA - startDateB
    })
  }, [allSpecialPeriods, selectedYear])

  const [currentSpecialPeriod, setCurrentSpecialPeriod] = React.useState<
    SpecialPeriod | 'new' | undefined
  >()

  return (
    <>
      <SidebarContentTitle
        title="Périodes Spéciales"
        onButtonClick={isSuperAdmin ? () => setCurrentSpecialPeriod('new') : undefined}
        addButtonLabel="Créer une période spéciale"
        buttonDataTestId="new-special-period"
        ButtonIcon={AddResourceIcon}
      />

      <Box>
        {/* Year filter */}
        {!isLoading && availableYears.length > 0 && (
          <Element mb={4}>
            <Form.Field>
              <Form.Label>Année</Form.Label>
              <Form.Select
                value={selectedYear || ''}
                onChange={e => {
                  const year = e.target.value ? parseInt(e.target.value, 10) : null
                  setSelectedYear(year)
                }}
              >
                <option value="">Toutes les années</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Form.Field>
          </Element>
        )}

        {/* List */}
        {isLoading ? (
          <Box>Chargement...</Box>
        ) : (
          <SpecialPeriodsList
            specialPeriods={specialPeriods || []}
            onPeriodClick={period => setCurrentSpecialPeriod(period)}
          />
        )}
      </Box>

      {/* Modal Form */}
      <SpecialPeriodModalForm
        isDisplayed={Boolean(currentSpecialPeriod)}
        setIsDisplayed={() => setCurrentSpecialPeriod(undefined)}
        specialPeriod={currentSpecialPeriod === 'new' ? undefined : currentSpecialPeriod}
        employerId={employer?._id}
        existingTypes={existingTypes}
      />
    </>
  )
}

export default SpecialPeriodsSettings
